import React, { Component } from 'react';
import { func, string, bool, shape, arrayOf } from 'prop-types';
import { validate } from 'ui-validator';
import LoginWidget from '../../../../shared_modules/rc-login-widget';
import { Page } from '../../components';
import { submitHeapTracking } from '../../utils/util';
import { heapTrackErrors, heapTrackEvent } from '@shared_modules/baui-heap-tracking';
import {
  PasswordInput,
  passwordValidations as validations,
  checkBlacklisted,
} from '../../../../shared_modules/local-propulsion/components';
import SecurityNotice from '../../components/SecurityNotice';

const BLACKLIST_HEAP_IDENTIFIER = 'PL_PI1';
let timeoutId;

class Password extends Component {
  state = {
    model: {
      password: this.props.password,
    },
    validationErrors: {},
    blacklistValidationsRan: false,
  };

  getTitleHeader = () => {
    const { isJoint } = this.props;
    const title = 'Set a password.';
    if (!this.props.isDM) {
      return {
        title,
        header: 'Secure your account with a password to save and access your application.',
        subText: '',
        submitLabel: 'Next',
        showDisclaimerContent: false,
      };
    }

    return {
      title: isJoint ? `Last step, ${this.props.primaryFirstName}! ${title}` : `Last step! ${title}`,
      header: 'Secure your account with a password to see your offer!',
      subText: 'Won’t impact your credit score.*',
      submitLabel: 'Get Your Rate',
      showDisclaimerContent: true,
    };
  };

  validateInput = () => {
    if (this.state.validationErrors.password && this.state.validationErrors.password.isBlacklisted) {
      return this.state.validationErrors;
    }

    return validate({ ...this.state.model, email: this.props.email }, validations);
  };

  handleInputChange = event => {
    const { name, value } = event.target;

    this.setState({
      model: {
        ...this.state.model,
        [name]: value,
      },
      blacklistValidationsRan: false,
    });
  };

  handleBlur = ({ name, validationErrors }) => {
    this.setState({
      validationErrors: {
        ...this.state.validationErrors,
        [name]: validationErrors[name],
      },
      blacklistValidationsRan: true,
    });
  };

  submitOnNextTick = e => {
    if (e) e.preventDefault();
    if (timeoutId) {
      clearTimeout(timeoutId); // To avoid multiple submit calls when user clicks multiple times.
    }
    timeoutId = setTimeout(() => {
      // for blacklisted pw check - make sure blur fires before submit when user clicks submit while focused in
      this.submit(e);
      // reconsider blacklistPw interaction to avoid multi calls but fire
      // events in order
    }, 500);
  };

  submit = async e => {
    if (e) e.preventDefault();

    if (this.props.isDM) submitHeapTracking(this.props.isJoint);

    const validationErrors = this.validateInput();
    if (Object.keys(validationErrors).length > 0) {
      this.setState({
        validationErrors,
      });
      heapTrackErrors(validationErrors);
    } else {
      if (!this.state.blacklistValidationsRan) {
        const isBlacklisted = await checkBlacklisted(this.state.model.password, BLACKLIST_HEAP_IDENTIFIER);
        if (isBlacklisted) {
          return this.setState({ blacklistValidationsRan: true });
        }
      }
      this.props.saveValues({
        ...this.state.model,
      });
      // not allowed to submit and send ajax request again. we disable the button but
      // user manipulating ui and allowing the button click will now cause a reload instead
      const lockButtons =
        this.props.isLoggedIn &&
        this.props.errors.some(error => ['LOAN_APP_IN_PROGRESS', 'REJECTED_BORROWER'].includes(error.error));
      if (lockButtons) {
        window.location.reload(true);
      } else if (!this.props.isAuthenticationRequired) {
        // not a MFA return user, using current PI1 submit button
        this.props.nextLocation();
      }
    }
  };

  getHeapProps = () => ({
    // today PBS is returning isAuthenticationRequired=false for returning users
    BORROWER_TYPE: this.props.isRepeatsUser ? 'REPEATS' : undefined,
  });

  render = () => {
    const validationErrors = {
      ...this.state.validationErrors,
      ...this.props.parsedEmailPasswordErrors,
    };
    const lockButtons =
      this.props.isLoggedIn &&
      this.props.errors.some(error => ['LOAN_APP_IN_PROGRESS', 'REJECTED_BORROWER'].includes(error.error));

    const { title, header, subText, submitLabel, showDisclaimerContent } = this.getTitleHeader();

    return this.props.isAuthenticationRequired ? (
      <LoginWidget
        email={this.props.email}
        firstName={this.props.primaryFirstName}
        isRepeatsUser={this.props.isRepeatsUser}
        isJoint={this.props.isJoint}
        onLogin={async err => {
          if (this.props.isDM) submitHeapTracking(this.props.isJoint);
          if (err) {
            // Error handling by default is done in the login
            // If you must override it, do so here.
          } else {
            await this.props.onLoginWidgetSuccess();
            if (!this.props.isRepeatsUser) {
              this.props.nextLocation();
            }
          }
        }}
        onRenderWidgetError={() => {}}
        onRenderWidgetSuccess={loginWidgetIFrame => {
          const passwordInput = loginWidgetIFrame.contentDocument.querySelector('input[name=password]');
          if (passwordInput) passwordInput.focus();
          loginWidgetIFrame.style.position = 'absolute';
          heapTrackEvent('Originations_In_Funnel_Login_Load', this.getHeapProps());
        }}
        forgotPasswordNewWindow
      />
    ) : (
      <Page
        title={title}
        header={header}
        name="password"
        goBack={lockButtons ? null : this.props.previousLocation}
        submitHandler={this.submitOnNextTick}
        disableSubmit={lockButtons}
        subText={subText}
        showDisclaimerContent={showDisclaimerContent}
        submitLabel={submitLabel}
      >
        <div className="form__input">
          <div className="form__input__email__label">{`${this.props.primaryFirstName}’s Email`}</div>
          <div className="form__input__email">{this.props.email}</div>
        </div>
        <div className="form__input password-container">
          <PasswordInput
            onBlur={this.handleBlur}
            onChange={this.handleInputChange}
            blackListHeapIdentifier={BLACKLIST_HEAP_IDENTIFIER}
            checkBlacklisted
            errors={validationErrors.password}
            password={this.state.model.password}
            autoFocus={!this.state.model.password}
            email={this.props.email}
            disabled={this.props.isLoggedIn}
          />
        </div>
        <SecurityNotice text="We’re committed to protecting your personal information and use bank-level encryption throughout our site." />
      </Page>
    );
  };
}

Password.propTypes = {
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  onLoginWidgetSuccess: func.isRequired,
  isJoint: bool.isRequired,
  isLoggedIn: bool.isRequired,
  primaryFirstName: string.isRequired,
  password: string.isRequired,
  email: string.isRequired,
  parsedEmailPasswordErrors: shape({}),
  isAuthenticationRequired: bool.isRequired,
  errors: arrayOf(shape({})).isRequired,
};

Password.defaultProps = {
  parsedEmailPasswordErrors: {},
};

export default Password;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pages/Password/Password.scss');